import { border, extendTheme } from "@chakra-ui/react";

const customTheme = extendTheme({
  fonts: {
    body: "Montserrat, sans-serif",
    heading: "Montserrat, sans-serif",
  },
  colors: {
    primaryBackground: "#f7fafc",
    secondaryBackground: "#ffffff",
    primaryHighlight: "#CDFF7A",
    secondaryHighlight: "#ee6c4d",
    infoBackground: "#F0FFF4",
    text: "#000000",
    heading: "#000000",
    white: "#FFFFFF",
    // lightGray: "#5A5A5A",
    lightGray: "#c6c6c6",
    warning: "#E53E3E",
    darkGray: "#6A6A7E",
    // Brand green — overrides Chakra's default green.400/500/600 so every
    // button, tab, badge, chart series etc. that references the green scale
    // picks up the brand color without per-site changes.
    green: {
      400: "#2CAC74", // lighter (button hover on light backgrounds, etc.)
      500: "#23865B", // primary brand accent
      600: "#1A6544", // darker (button _hover)
    },
  },

  components: {
    Button: {
      variants: {
        // Standard primary action color: deep brand green. The old light
        // `primaryHighlight` (#CDFF7A) is still available as a color token
        // for use as backgrounds on non-button elements — this change only
        // affects the Button variant.
        primary: {
          bg: "green.500",
          color: "white",
          _hover: {
            bg: "green.600",
            color: "white",
          },
          _disabled: {
            bg: "green.500",
            cursor: "not-allowed",
            _hover: {
              bg: "green.600 !important",
            },
          },
        },
        secondary: {
          bg: "green.500",
          color: "white",
          _hover: {
            bg: "green.600",
            color: "white",
          },
          _disabled: {
            bg: "green.500",
            cursor: "not-allowed",
            _hover: {
              bg: "green.600 !important",
            },
          },
        },
        outline: {
          borderColor: "black",
          color: "black",
          borderWidth: "1px",
          _hover: {
            borderColor: "black",

            color: "white",
            bg: "black",
          },
        },
        secondaryOutline: {
          borderColor: "secondaryHighlight",
          color: "secondaryHighlight",
          borderWidth: "1px",
          _hover: {
            borderColor: "secondaryHighlight",
            color: "secondaryHighlight",
            bg: "#d2f0fe",
          },
        },
        outlineNoBorder: {
          borderColor: "none",
          color: "lightGray",
          _hover: {
            borderColor: "none",
            color: "white",
            bg: "none",
          },
        },

        nav: {
          color: "white",
          _hover: {
            color: "#c1c8d2",
          },
        },
        navLanding: {
          color: "black",
          _hover: {
            color: "gray.400",
          },
        },
        link: {
          color: "text",
          _hover: {
            color: "text",
            textDecoration: "underline",
          },
        },
        linkSecondary: {
          color: "white",
          textDecoration: "underline",
          _hover: {
            color: "white",
            textDecoration: "none",
          },
        },
        stealth: {
          bg: "transparent",
          color: "black",

          _hover: {
            bg: "gray.100",
            color: "black",
          },
        },
        google: {
          // border: "1px solid",
          // borderColor: "lightGray",
          bg: "#4285f4",
          color: "white",
          _hover: {
            // border: "1px solid",
            // borderColor: "lightGray",
            bg: "#a9c7fa",
            color: "white",
          },
        },
      },
    },

    Input: {
      baseStyle: {
        field: {
          bg: "transparent",
          color: "text !important",
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "lightGray",
            _hover: {
              borderColor: "lightGray",
            },
            _focus: {
              borderColor: "black !important",
              boxShadow: "0 0 0 1px black !important",
              outline: "none !important",
            },
          },
        },
      },
      defaultProps: {
        variant: "outline",
        focusBorderColor: "black",
      },
    },

    Textarea: {
      baseStyle: {
        field: {
          bg: "transparent",
          color: "text !important",
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "lightGray",
            _hover: {
              borderColor: "lightGray",
            },
            _focus: {
              borderColor: "black !important",
              boxShadow: "0 0 0 1px black !important",
              outline: "none !important",
            },
          },
        },
      },
      defaultProps: {
        variant: "outline",
        focusBorderColor: "black",
      },
    },

    Progress: {
      baseStyle: {
        filledTrack: {
          bg: "primaryHighlight",
        },
      },
    },

    Checkbox: {
      defaultProps: {
        colorScheme: "green",
      },
    },

    // Filled-primary tab variant used on the site's main dashboards
    // (CampaignHome, PlatformDashboard). Selected tab is filled with the
    // deep brand green with white text; unselected tabs stay neutral. The
    // tab list itself carries a light bottom border so the selected chip
    // reads as sitting on the strip.
    Tabs: {
      variants: {
        brand: {
          tablist: {
            borderBottomWidth: "5px",
            borderColor: "green.500",
          },
          tab: {
            color: "gray.600",
            fontWeight: "medium",
            borderTopRadius: "md",
            // No negative margin — selected tab sits ABOVE the 5px line so
            // the strip stays unbroken and clearly visible under every tab.
            _selected: {
              bg: "green.500",
              color: "white",
              borderColor: "green.500",
              borderBottomColor: "green.500",
            },
            _hover: {
              color: "green.700",
              _selected: {
                color: "white",
              },
            },
          },
        },
      },
    },

    Stepper: {
      baseStyle: {
        indicator: {
          borderColor: "lightGray",
        },
        separator: {
          borderColor: "lightGray",
        },
        stepIconContainer: {
          bg: "white",
          borderColor: "lightGray",
          borderWidth: "2px",
          "&[data-status=complete]": {
            bgColor: "red",
            borderColor: "red",
          },
          "&[data-status=active]": {
            bgColor: "red",
            borderColor: "red",
          },
        },
        label: {
          color: "darkGray",
          fontWeight: "medium",
          _active: {
            color: "text",
            fontWeight: "bold",
          },
          _complete: {
            color: "text",
            fontWeight: "bold",
          },
        },
        number: {
          fontSize: "sm",
          color: "darkGray",
          _active: {
            color: "black",
          },
          _complete: {
            color: "black",
          },
        },
        icon: {
          color: "black",
        },
      },
      sizes: {
        sm: {
          stepIconContainer: {
            width: "6",
            height: "6",
          },
        },
        md: {
          stepIconContainer: {
            width: "8",
            height: "8",
          },
        },
        lg: {
          stepIconContainer: {
            width: "10",
            height: "10",
          },
        },
      },
      defaultProps: {
        size: "md",
        colorScheme: "primary",
      },
    },

    Step: {
      baseStyle: {
        _active: {
          "& .chakra-step__indicator": {
            borderColor: "primaryHighlight",
            bg: "primaryHighlight",
          },
          "& .chakra-step__title": {
            color: "text",
            fontWeight: "bold",
          },
          "& .chakra-step__number": {
            color: "black",
          },
        },
        _complete: {
          "& .chakra-step__indicator": {
            borderColor: "primaryHighlight",
            bg: "primaryHighlight",
          },
          "& .chakra-step__separator": {
            borderColor: "primaryHighlight",
          },
          "& .chakra-step__title": {
            color: "text",
            fontWeight: "bold",
          },
        },
        _invalid: {
          "& .chakra-step__indicator": {
            borderColor: "warning",
            bg: "white",
          },
          "& .chakra-step__separator": {
            borderColor: "warning",
          },
          "& .chakra-step__title": {
            color: "warning",
          },
        },
      },
    },
  },
});

export default customTheme;
